import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { AdminProductResponse, CreateOrUpdateProductRequest, ProductResponse } from "./productsApi";
import { cloneProduct, deleteProduct, updateProduct } from "./productsApi";
import { useProductWithId } from "./useProductWithId";

// Both consumers of this hook (EditProductPage, ReadOnlyProductPage) render only under the admin/products routes,
// reachable only by ADMIN/AGENT — see AdminProductResponse in productsApi.ts for why that lets us narrow the
// role-trimmed fields to always-present. useProductWithId itself stays untyped for this since it also backs
// customer-reachable call sites (e.g. calculations2's option price dialog) that must not assume admin/agent data.
export const useProduct = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { productId } = useParams<{ productId: string }>();
  const { product, isLoading } = useProductWithId(productId);
  const adminProduct = product as AdminProductResponse | undefined;

  const cloneProductMutation = useMutation<ProductResponse, unknown, void>({
    mutationFn: () => cloneProduct(productId!),
    onSuccess: async (data) => {
      enqueueSnackbar(`Kopia produktu została utworzona`, { variant: "success" });
      queryClient.setQueryData(["products", data.id], data);
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się stworzyć kopii produktu. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateProductMutation = useMutation<ProductResponse, unknown, CreateOrUpdateProductRequest>({
    mutationFn: (p) => updateProduct(productId!, p),
    onSuccess: async (data) => {
      enqueueSnackbar(`Produkt został zaktualizowany`, { variant: "success" });
      queryClient.setQueryData(["products", productId], data);
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować produktu. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteProductMutation = useMutation<void, unknown, void>({
    mutationFn: () => deleteProduct(productId!),
    onSuccess: async () => {
      enqueueSnackbar(`Produkt został usunięty`, { variant: "success" });
      queryClient.removeQueries({ queryKey: ["products", productId] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć produktu. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    product: adminProduct,
    isLoading,
    deleteProduct: () => deleteProductMutation.mutateAsync(),
    cloneProduct: () => cloneProductMutation.mutateAsync(),
    updateProduct: (request: CreateOrUpdateProductRequest) => updateProductMutation.mutateAsync(request),
  };
};
